import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { LOGO, FLAVORS } from "@/lib/catalog";

// Masked line-by-line reveal
const LINES = ["RECEITA", "DO BROWNIE", "QUE TE MOVE."];

const line = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: 0,
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fade = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.8, delay: 0.9 + i * 0.1 } }),
};

export default function RecipeHero({ onCta }) {
  return (
    <section
      data-testid="recipe-hero"
      className="relative min-h-[94vh] md:min-h-screen flex flex-col overflow-hidden bg-white"
    >
      {/* Top bar */}
      <div className="mx-auto max-w-[1500px] w-full px-6 md:px-10 pt-6 md:pt-8 flex items-center justify-between">
        <a href="/" className="flex items-center gap-3" aria-label="The Quick Brownie">
          <img src={LOGO} alt="" className="h-8 md:h-10 w-auto" />
          <span className="font-display text-base md:text-lg tracking-tight">The Quick Brownie</span>
        </a>
        <motion.span
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 0.8 }}
          className="text-[10px] md:text-xs uppercase tracking-[0.25em]"
        >
          Edição digital · PDF
        </motion.span>
      </div>

      {/* Big masked headline */}
      <div className="flex-1 flex items-center">
        <div className="mx-auto max-w-[1500px] w-full px-6 md:px-10 py-8">
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}
            className="flex items-center gap-4 text-[10px] md:text-xs tracking-[0.3em] uppercase"
          >
            <span className="inline-block w-10 h-px bg-black" />
            <span>Edição I · The Quick Brownie</span>
          </motion.div>

          <h1
            data-testid="recipe-hero-title"
            className="font-display mt-6 md:mt-10 leading-[0.86] tracking-[-0.035em] text-[46px] sm:text-7xl md:text-[112px] lg:text-[156px]"
          >
            {LINES.map((text, i) => (
              <span
                key={i}
                className="block overflow-hidden"
                aria-label={text}
              >
                <motion.span
                  className="block will-change-transform"
                  custom={i}
                  initial="hidden"
                  animate="visible"
                  variants={line}
                >
                  {i === LINES.length - 1 ? (
                    <>
                      {text.split(" ")[0]}{" "}
                      <span className="italic">{text.split(" ").slice(1).join(" ")}</span>
                    </>
                  ) : text}
                </motion.span>
              </span>
            ))}
          </h1>

          <div className="mt-10 md:mt-16 grid md:grid-cols-12 gap-8 md:gap-10 items-end">
            <motion.p
              custom={0} initial="hidden" animate="visible" variants={fade}
              className="md:col-span-6 text-base md:text-lg leading-relaxed max-w-xl"
            >
              Produtos prontos de qualidade, sem açúcar, sem glúten e sem lactose,
              custam caro — e são difíceis de encontrar. Agora você faz na sua
              cozinha, do seu jeito, no seu tempo.
            </motion.p>

            <motion.div
              custom={1} initial="hidden" animate="visible" variants={fade}
              className="md:col-span-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 md:justify-end"
            >
              <button data-testid="hero-cta-buy" onClick={onCta} className="btn-primary">
                Quero a receita
                <ArrowDown size={16} strokeWidth={2} />
              </button>
              <a href="#beneficios" className="btn-outline">
                Ver o que você aprende
              </a>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Floating product strip — subtle parallax hero moment */}
      <motion.div
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 1, ease: [0.22, 1, 0.36, 1] }}
        className="pointer-events-none select-none w-full border-t border-black"
      >
        <div className="mx-auto max-w-[1500px] px-6 md:px-10 py-5 md:py-6 flex items-center gap-5 overflow-x-auto no-scrollbar">
          {FLAVORS.map((f, i) => (
            <motion.div
              key={f.slug}
              whileInView={{ y: [8, 0], opacity: [0, 1] }}
              viewport={{ once: true }}
              transition={{ delay: 1.3 + i * 0.08, duration: 0.6 }}
              className="shrink-0 w-24 h-24 md:w-28 md:h-28 border border-black overflow-hidden bg-white"
            >
              <img src={f.img} alt={f.label} className="w-full h-full object-cover" loading="eager" />
            </motion.div>
          ))}
          <div className="shrink-0 ml-auto text-[10px] md:text-xs uppercase tracking-[0.25em] hidden md:block">
            Recheie como quiser ↑
          </div>
        </div>
      </motion.div>
    </section>
  );
}
